import React, { useRef, useEffect, useCallback } from 'react';
import Player from '@vimeo/player';
import { updateUser } from '../../util/db';

const THROTTLE_INTERVAL_MS = 15000; // Report every 15 seconds

const VideoCard = ({
  embedCode,
  title,
  description,
  uid,
  postId,
  prev,
  codeId,           // NEW: video_codes document ID for watch tracking
  onTimeUpdate,     // NEW: callback to report watched seconds
}) => {
  const iframeRef = useRef(null);
  const lastReportedTimeRef = useRef(0);
  const pendingSecondsRef = useRef(0);
  const throttleTimerRef = useRef(null);

  // Flush pending seconds to parent
  const flushPendingSeconds = useCallback(() => {
    if (pendingSecondsRef.current > 0 && onTimeUpdate) {
      onTimeUpdate(pendingSecondsRef.current);
      pendingSecondsRef.current = 0;
    }
  }, [onTimeUpdate]);

  useEffect(() => {
    const player = new Player(iframeRef.current);

    // Handle video end - track completion
    player.on('ended', () => {
      // Flush any remaining watch time
      flushPendingSeconds();

      // Note: For "sb" videos, `prev` is passed as auth.user.IpSbCount (a number)
      // For other videos, `prev` is passed as auth.user.finishedContent (an array)
      // ResourceCompletionTable checks IpSbCount for "sb" team tracking
      if (postId === "sb") {
        updateUser(uid, {
          IpSbCount: prev ? prev + 1 : 1
        });
      } else if (postId && uid) {
        if (prev && prev.includes(postId)) {
          console.log("already watched");
        } else if (prev) {
          updateUser(uid, {
            finishedContent: [postId, ...prev]
          });
        } else {
          updateUser(uid, {
            finishedContent: [postId]
          });
        }
      }
    });

    // Handle time updates for watch tracking (only if codeId provided)
    if (codeId && onTimeUpdate) {
      player.on('timeupdate', (data) => {
        const currentTime = Math.floor(data.seconds);
        const secondsWatched = currentTime - lastReportedTimeRef.current;

        if (secondsWatched > 0) {
          pendingSecondsRef.current += secondsWatched;
          lastReportedTimeRef.current = currentTime;
        }

        // Throttle: only report every THROTTLE_INTERVAL_MS
        if (!throttleTimerRef.current) {
          throttleTimerRef.current = setTimeout(() => {
            flushPendingSeconds();
            throttleTimerRef.current = null;
          }, THROTTLE_INTERVAL_MS);
        }
      });
    }

    // Handle seeking (user skips forward/backward)
    player.on('seeked', (data) => {
      lastReportedTimeRef.current = Math.floor(data.seconds);
    });

    return () => {
      // Cleanup: flush remaining seconds and clear timer
      flushPendingSeconds();
      if (throttleTimerRef.current) {
        clearTimeout(throttleTimerRef.current);
      }
      player.off('ended');
      player.off('timeupdate');
      player.off('seeked');
    };
  }, [uid, postId, prev, codeId, onTimeUpdate, flushPendingSeconds]);

  // Flush on page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      flushPendingSeconds();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [flushPendingSeconds]);

  return (
    <div className="border border-gray-300 rounded-lg p-4 w-full mx-auto">
      <div className="mb-4">
        <iframe
          ref={iframeRef}
          src={"https://player.vimeo.com/video/" + embedCode + "?&title=0&byline=0&portrait=0&badge=0&dnt=1"}
          width="100%"
          height="400"
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          title="Vimeo Video"
        ></iframe>
      </div>
      <h2 className="text-xl font-semibold mb-2">{title}</h2>
      <p className="text-gray-700">{description}</p>
    </div>
  );
};

export default VideoCard;
